import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { API, useAuth } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { fetchDisclaimer, postDisclaimerConsent } from "../api/disclaimer";
import { isDisclaimerRequired } from "../lib/disclaimerConsent";
import { FEATURE_PRIVACY_POLICY } from "../config/featureFlags";
import { LEGAL_PRIVACY_URL } from "../config/legalUrls";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CONSUMER_HOME_PATH } from "../routing/b2cAccess";

const SEX_OPTIONS = ["female", "male", "intersex", "prefer_not_to_say"];
const PREGNANCY_OPTIONS = ["not_applicable", "yes", "possibly", "prefer_not_to_say"];

export function buildTierAPayload({
  year,
  sexAtBirth,
  genderIdentity,
  pregnancyStatus,
  showPregnancy,
}) {
  const payload = {
    birth_year: year,
    sex_at_birth: sexAtBirth,
  };
  if (genderIdentity.trim()) payload.gender_identity = genderIdentity.trim();
  payload.pregnancy_status = showPregnancy
    ? pregnancyStatus || "not_applicable"
    : "not_applicable";
  return payload;
}

/**
 * Tier A onboarding — required demographics before consumer home.
 */
const ProfileOnboardingPage = () => {
  const { t, language } = useLanguage();
  const { fetchBackendUser } = useAuth();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const [birthYear, setBirthYear] = useState("");
  const [sexAtBirth, setSexAtBirth] = useState("");
  const [genderIdentity, setGenderIdentity] = useState("");
  const [pregnancyStatus, setPregnancyStatus] = useState("not_applicable");
  const [disclaimerText, setDisclaimerText] = useState("");
  const [textVersion, setTextVersion] = useState("");
  const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(false);
  const [consentSaved, setConsentSaved] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [saving, setSaving] = useState(false);

  const showPregnancy = useMemo(
    () => ["female", "intersex", "prefer_not_to_say"].includes(sexAtBirth),
    [sexAtBirth],
  );

  useEffect(() => {
    let cancelled = false;
    fetchDisclaimer(language)
      .then((payload) => {
        if (cancelled) return;
        setDisclaimerText(payload.text.trim());
        setTextVersion(payload.text_version);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("healthProfile.saveError"));
      });
    return () => {
      cancelled = true;
    };
    // t is only the error toast; it is a new function every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const onDisclaimerChecked = async (checked) => {
    if (!checked) {
      setDisclaimerAcknowledged(false);
      setConsentSaved(false);
      return;
    }
    if (!textVersion || consentSaving || saving) return;
    setConsentSaving(true);
    try {
      await postDisclaimerConsent({
        text_version: textVersion,
        platform: "web",
        locale: language,
      });
      setDisclaimerAcknowledged(true);
      setConsentSaved(true);
    } catch (err) {
      setDisclaimerAcknowledged(false);
      setConsentSaved(false);
      toast.error(
        isDisclaimerRequired(err)
          ? t("query.disclaimerRequired")
          : t("healthProfile.disclaimerRequired"),
      );
    } finally {
      setConsentSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const year = Number.parseInt(birthYear, 10);
    if (!Number.isFinite(year) || !sexAtBirth) {
      toast.error(t("healthProfile.tierARequired"));
      return;
    }
    if (!consentSaved) {
      toast.error(t("healthProfile.disclaimerRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = buildTierAPayload({
        year,
        sexAtBirth,
        genderIdentity,
        pregnancyStatus,
        showPregnancy,
      });

      const res = await axios.put(`${API}/profile/health/tier-a`, payload);
      if (!res.data?.data?.tier_a_complete) {
        toast.error(t("healthProfile.tierARequired"));
        return;
      }
      await fetchBackendUser?.();
      toast.success(t("healthProfile.tierASaved"));
      navigate(CONSUMER_HOME_PATH, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || d).join("; ")
            : t("healthProfile.saveError");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center p-4"
      data-testid="profile-onboarding-page"
    >
      <Card className="w-full max-w-lg border border-border">
        <CardHeader>
          <CardTitle className="text-xl">{t("healthProfile.tierATitle")}</CardTitle>
          <CardDescription>{t("healthProfile.tierADesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="birth_year">{t("healthProfile.birthYear")}</Label>
              <Input
                id="birth_year"
                data-testid="health-birth-year"
                type="number"
                inputMode="numeric"
                min={currentYear - 120}
                max={currentYear - 18}
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                placeholder={String(currentYear - 40)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>{t("healthProfile.sexAtBirth")}</Label>
              <Select value={sexAtBirth} onValueChange={setSexAtBirth}>
                <SelectTrigger data-testid="health-sex-at-birth">
                  <SelectValue placeholder={t("healthProfile.selectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {SEX_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {t(`healthProfile.sex.${opt}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender_identity">{t("healthProfile.genderIdentity")}</Label>
              <Input
                id="gender_identity"
                value={genderIdentity}
                onChange={(e) => setGenderIdentity(e.target.value)}
                placeholder={t("healthProfile.genderIdentityPlaceholder")}
              />
            </div>

            {showPregnancy && (
              <div className="space-y-2">
                <Label>{t("healthProfile.pregnancyStatus")}</Label>
                <Select value={pregnancyStatus} onValueChange={setPregnancyStatus}>
                  <SelectTrigger data-testid="health-pregnancy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PREGNANCY_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {t(`healthProfile.pregnancy.${opt}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-start gap-3" data-testid="disclaimer-ack-block">
              <Checkbox
                id="disclaimer-ack"
                data-testid="disclaimer-ack-checkbox"
                checked={disclaimerAcknowledged}
                onCheckedChange={(checked) => onDisclaimerChecked(checked === true)}
                disabled={saving || consentSaving}
              />
              <label
                htmlFor="disclaimer-ack"
                className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                data-testid="disclaimer-ack-text"
              >
                {disclaimerText}
              </label>
            </div>

            <p className="text-xs text-muted-foreground">
              {t("healthProfile.privacyNote")}{" "}
              {FEATURE_PRIVACY_POLICY ? (
                <a
                  href={LEGAL_PRIVACY_URL}
                  className="underline underline-offset-2"
                  rel="noopener noreferrer"
                  data-testid="onboarding-privacy-link"
                >
                  {t("healthProfile.privacyLink")}
                </a>
              ) : null}
            </p>

            <Button
              type="submit"
              className="w-full"
              disabled={saving || consentSaving || !consentSaved}
              data-testid="health-tier-a-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("healthProfile.continue")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileOnboardingPage;
